import type { MessageCallbacks } from './types';

export function handleMlPrediction(obj: any, cb: MessageCallbacks): boolean {
  if (!((obj?.kind === 'ML_PREDICTION' || obj?.type === 'ml_prediction') && (obj.data?.predictions || obj.predictions))) return false;

  const data = obj.data || obj;
  const secId = ((data.sec_id || data.secId || obj.secId) || '').toUpperCase();
  const pred = data.predictions || obj.predictions;
  if (!secId) return true;

  cb.lastMlFillUpdateMsBySecIdRef.current.set(secId, Date.now());

  if (secId === cb.selectedSymbol) {
    const fillProb = Number(pred.fill_prob ?? 0);
    const fillProbBid = pred.fill_prob_bid != null ? Number(pred.fill_prob_bid) : fillProb;
    const fillProbAsk = pred.fill_prob_ask != null ? Number(pred.fill_prob_ask) : fillProb;
    const timeToTrade = Number(pred.time_to_trade ?? 0);
    const confNum = Number(pred.fill_prob_confidence ?? 0.5);
    const confidence: 'low' | 'medium' | 'high' = confNum >= 0.66 ? 'high' : confNum >= 0.33 ? 'medium' : 'low';

    const mkDetail = (prob: number) => ({
      probability: prob, confidence, estimated_wait_time: timeToTrade,
      queue_position: 0, model_version: '', model_type: '', timestamp: '',
    });

    cb.setAnalytics(prev => {
      const base = prev ?? cb.getMinimalAnalytics();
      return {
        ...base,
        fillProbability: {
          ...base.fillProbability,
          probability: fillProb,
          bidProbability: fillProbBid,
          askProbability: fillProbAsk,
          estimatedWaitTime: timeToTrade,
          confidence,
        },
        ml: {
          ...base.ml,
          enabled: true,
          fill_probability: {
            buy: { at_best_ask: fillProbBid, at_mid: fillProb, at_best_bid: 0 },
            sell: { at_best_bid: fillProbAsk, at_mid: fillProb, at_best_ask: 0 },
            details: { buy_at_ask: mkDetail(fillProbBid), sell_at_bid: mkDetail(fillProbAsk) },
          },
        },
      };
    });
  }
  return true;
}

export function handleRecentTrades(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'RECENT_TRADES' && obj.secId && Array.isArray(obj.trades))) return false;

  const secId = (obj.secId || '').toUpperCase();
  if (!secId) return true;

  const trades = obj.trades
    .map((t: any) => ({
      time: t.time || new Date().toISOString(),
      price: Number(t.price) || 0,
      size: Number(t.size) || 0,
      side: (t.side?.toLowerCase() === 'sell' || t.side === 'SELL') ? 'sell' : 'buy',
      symbol: secId,
    }))
    .filter((t: any) => t.price > 0 && t.size > 0)
    .sort((a: any, b: any) => {
      const ta = new Date(a.time).getTime();
      const tb = new Date(b.time).getTime();
      return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
    });

  cb.tradesBySecIdRef.current.set(secId, trades);

  if (secId === cb.selectedSymbol) {
    cb.setRecentTrades(trades.slice(0, 10));
    if (trades.length > 0) cb.setCurrentPrice(trades[0].price);
  }

  const ts = (() => {
    if (!obj.timestamp) return new Date().toISOString();
    if (typeof obj.timestamp === 'string') return obj.timestamp;
    return obj.timestamp < 1e12 ? new Date(obj.timestamp * 1000).toISOString() : new Date(obj.timestamp).toISOString();
  })();

  cb.lastUpdateBySecIdRef.current.set(secId, ts);
  if (secId === cb.selectedSymbol) cb.setLastUpdateTime(ts);

  return true;
}
